import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error Boundary component to catch JavaScript errors anywhere in the component tree
 * and display a fallback UI instead of crashing the entire app.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Log error to console
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Call optional error handler
        this.props.onError?.(error, errorInfo);

        // TODO: Send to error monitoring service (Sentry, etc.)
        // if (typeof Sentry !== 'undefined') {
        //     Sentry.captureException(error, { extra: errorInfo });
        // }
    }

    handleReset = (): void => {
        this.setState({ hasError: false, error: null });
    };

    render(): ReactNode {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <ErrorFallback
                    error={this.state.error}
                    onReset={this.handleReset}
                />
            );
        }

        return this.props.children;
    }
}

// Props for fallback components
interface ErrorFallbackProps {
    error?: Error | null;
    onReset?: () => void;
    title?: string;
    message?: string;
}

/**
 * Generic Error Fallback UI
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({
    error,
    onReset,
    title = 'Đã xảy ra lỗi',
    message = 'Xin lỗi, ứng dụng đã gặp sự cố. Vui lòng thử lại.',
}) => {
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
            <div className="text-center max-w-md">
                {/* Error Icon */}
                <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                    <svg
                        className="w-8 h-8 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                </div>

                {/* Title */}
                <h1 className="text-xl font-semibold text-white mb-2">{title}</h1>

                {/* Message */}
                <p className="text-white/70 mb-6">{message}</p>

                {/* Error details (dev only) */}
                {import.meta.env.DEV && error && (
                    <details className="mb-6 text-left">
                        <summary className="text-sm text-white/50 cursor-pointer hover:text-white/70">
                            Chi tiết lỗi (Dev)
                        </summary>
                        <pre className="mt-2 p-3 bg-black/50 rounded-lg text-xs text-red-400 overflow-auto max-h-32">
                            {error.message}
                            {'\n\n'}
                            {error.stack}
                        </pre>
                    </details>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {onReset && (
                        <button
                            onClick={onReset}
                            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors min-h-[44px]"
                        >
                            Thử lại
                        </button>
                    )}
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors min-h-[44px]"
                    >
                        Tải lại trang
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Calendar-specific Error Fallback
 */
const CalendarErrorFallback: React.FC<ErrorFallbackProps> = ({ onReset }) => {
    return (
        <div className="h-full flex items-center justify-center bg-gradient-to-b from-slate-800 to-slate-900 p-4">
            <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-orange-500/20 rounded-full flex items-center justify-center">
                    <svg
                        className="w-6 h-6 text-orange-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                </div>
                <h2 className="text-lg font-medium text-white mb-2">
                    Không thể tải lịch
                </h2>
                <p className="text-white/60 text-sm mb-4">
                    Vui lòng thử lại hoặc tải lại trang
                </p>
                {onReset && (
                    <button
                        onClick={onReset}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors min-h-[44px]"
                    >
                        Thử lại
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Note-specific Error Fallback
 */
const NoteErrorFallback: React.FC<ErrorFallbackProps> = ({ onReset }) => {
    return (
        <div className="h-full flex items-center justify-center bg-slate-800 p-4">
            <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <svg
                        className="w-6 h-6 text-yellow-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                    </svg>
                </div>
                <h2 className="text-lg font-medium text-white mb-2">
                    Lỗi ghi chú
                </h2>
                <p className="text-white/60 text-sm mb-4">
                    Không thể tải hoặc lưu ghi chú
                </p>
                {onReset && (
                    <button
                        onClick={onReset}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors min-h-[44px]"
                    >
                        Thử lại
                    </button>
                )}
            </div>
        </div>
    );
};

export {
    ErrorBoundary,
    ErrorFallback,
    CalendarErrorFallback,
    NoteErrorFallback,
};
export type { ErrorBoundaryProps, ErrorFallbackProps };
